import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useAnalytics } from '@backstage/core-plugin-api';
import { gitlabAuthApiRef } from '@backstage/core-plugin-api';
import { Content, Header, Page, Progress, UserIdentity } from '@backstage/core-components';
import type { SignInPageProps } from '@backstage/core-plugin-api';
import { useMountEffect } from '@react-hookz/web';
import { formatSignInPageError } from '../../utils/oauthLoginErrorHints';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 'calc(100vh - 200px)',
    padding: theme.spacing(4),
    // Soft gradient background (brand blue tint) so it's not plain white
    background: 'linear-gradient(160deg, #e8eef5 0%, #f0f5fa 45%, #fafafa 100%)',
    backgroundAttachment: 'fixed',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4, 5),
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[4],
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    maxWidth: 400,
  },
  welcome: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    textAlign: 'center',
    fontWeight: 500,
  },
  button: {
    padding: theme.spacing(2, 4),
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: theme.shadows[2],
    '&:hover': {
      boxShadow: theme.shadows[4],
    },
  },
  error: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
    textAlign: 'center',
    whiteSpace: 'pre-line',
  },
}));

/** Fewer probes → fewer parallel `GET …/gitlab/refresh?optional` calls (GitLab may rotate refresh tokens). */
const IDENTITY_POST_OAUTH_ATTEMPTS = 3;
const IDENTITY_POST_OAUTH_DELAY_MS = 500;

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function CustomSignInPage(props: SignInPageProps) {
  const { onSignInSuccess } = props;
  const classes = useStyles();
  const configApi = useApi(configApiRef);
  const authApi = useApi(gitlabAuthApiRef);
  const analytics = useAnalytics();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const handleSignIn = async () => {
    setError(null);
    try {
      // Start OAuth immediately on click so the browser still treats window.open as
      // user-initiated. Do not call getBackstageIdentity({ optional: true }) in a
      // loop first — that path never creates a session and delays past the gesture
      // window, which blocks the GitLab popup.
      await authApi.signIn();
      const identityResponse = await authApi.getBackstageIdentity();
      if (!identityResponse) {
        throw new Error('GitLab sign-in was not completed.');
      }
      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
      analytics.captureEvent('signIn', 'success');
    } catch (err) {
      setError(new Error(formatSignInPageError(err)));
    }
  };

  useMountEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let identityResponse = null;
        for (let attempt = 1; attempt <= IDENTITY_POST_OAUTH_ATTEMPTS; attempt++) {
          try {
            identityResponse = await authApi.getBackstageIdentity({
              optional: true,
            });
            if (identityResponse) {
              break;
            }
          } catch {
            /* cookie / edge auth not ready */
          }
          if (cancelled) {
            return;
          }
          await sleep(IDENTITY_POST_OAUTH_DELAY_MS);
        }
        if (cancelled) return;
        if (identityResponse) {
          const profile = await authApi.getProfile();
          onSignInSuccess(
            UserIdentity.create({
              identity: identityResponse.identity,
              authApi,
              profile,
            }),
          );
          analytics.captureEvent('signIn', 'success');
          return;
        }
      } catch {
        // No existing session, show sign-in button
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  });

  if (loading) {
    return <Progress />;
  }

  return (
    <Page themeId="home">
      <Header title={configApi.getString('app.title')} />
      <Content>
        <div className={classes.root}>
          <div className={classes.card}>
            <Typography className={classes.welcome} variant="h6" component="p">
              Welcome to Iron Phoenix
            </Typography>
            <Typography className={classes.welcome} variant="body2" component="p" style={{ marginBottom: 8 }}>
              Sign in with your GitLab account to continue.
            </Typography>
            <Button
              className={classes.button}
              color="primary"
              variant="contained"
              size="large"
              onClick={handleSignIn}
            >
              Sign in via GitLab
            </Button>
            {error && (
              <Typography className={classes.error} variant="body2">
                {error.message}
              </Typography>
            )}
          </div>
        </div>
      </Content>
    </Page>
  );
}
